import type { components } from "@brandex/api-client";
import { formatDate, formatRelative, formatTaka, type MessageKey } from "@brandex/i18n";
import { Button, Spinner, cn } from "@brandex/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

import { useBrandex, useT } from "../context";
import { errorMessage } from "../errors";
import { shootWhen } from "../views/content";

type Item = components["schemas"]["NotificationDto"];
type Inbox = components["schemas"]["InboxDto"];

export const bellKey = ["me", "notifications"] as const;

/** The notification's sentence in the reader's language. */
export function notificationText(t: (k: MessageKey, v?: Record<string, string | number>) => string, locale: "en" | "bn", n: Item): string {
  const p = Object.fromEntries(n.params.map((x) => [x.key, x.value]));
  const when = p.starts_at ? shootWhen(locale, p.starts_at) : (p.when ?? "");
  const vars: Record<string, string> = {
    title: p.title ?? "",
    doctor: p.doctor ?? "",
    author: p.author ?? "",
    comment: p.comment ?? "",
    patient: p.patient ?? "",
    chamber: p.chamber ?? "",
    when,
    platform: p.platform ? t(`content.platform.${p.platform}` as MessageKey) : "",
    shoot: p.shoot ? t(`content.shoot.kind.${p.shoot}` as MessageKey) : "",
    period: p.period ?? "",
    due_on: p.due_on ?? "",
    amount: p.amount_paisa ? formatTaka(locale, Number(p.amount_paisa)) : "",
    days: p.days ?? "",
    note: p.note ?? "",
    // The package runs through the day before it ends.
    ends_at: p.ends_at ? formatDate(locale, new Date(Date.parse(p.ends_at) - 1000).toISOString()) : "",
  };
  const key = (
    n.kind === "changes_requested" && vars.comment === ""
      ? "notify.changes_requested_plain"
      : n.kind === "package_cancelled" && vars.ends_at !== ""
        ? "notify.package_cancelled_until"
        : n.kind === "article_submitted" && vars.author === ""
          ? "notify.article_submitted_plain"
          : n.kind === "template_request_updated"
          ? `notify.template_request_updated.${p.status ?? "in_progress"}`
            : n.kind === "practice_updated_by_staff"
              ? `notify.practice_updated_by_staff.${p.area ?? "profile"}`
              : `notify.${n.kind}`
  ) as MessageKey;
  return t(key, vars);
}

/** Notifications that mean a content item moved. */
const CONTENT_KINDS = new Set<string>(["content_sent_to_you", "content_approved", "changes_requested", "content_published"]);
/** Notifications that mean an article moved. */
const ARTICLE_KINDS = new Set<string>(["article_submitted", "article_changes_requested", "article_published"]);

/**
 * The bell (F-13): unread count, the latest notifications, mark read on
 * open. Used by the doctor portal and the admin panel.
 */
export function NotificationBell({ className }: { className?: string }) {
  const t = useT();
  const { api, nav, locale } = useBrandex();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const panel = useId();
  const root = useRef<HTMLDivElement>(null);
  const inbox = useQuery<Inbox>({
    queryKey: bellKey,
    queryFn: async () => {
      const { data } = await api.http.GET("/v1/me/notifications");
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
    refetchInterval: 30_000,
  });
  // A new content notification (sent to you, approved, changes, published)
  // refreshes the content lists at once, wherever the user is.
  const newest = inbox.data?.items[0];
  const newestContent = newest !== undefined && CONTENT_KINDS.has(newest.kind) ? newest.id : null;
  const newestArticle = newest !== undefined && ARTICLE_KINDS.has(newest.kind) ? newest.id : null;
  useEffect(() => {
    if (newestArticle === null) return;
    // The doctor's lists (["orgs", id, "articles", …]) and the admin queue (["admin", "articles", …]).
    void qc.invalidateQueries({ predicate: (q) => q.queryKey.includes("articles") });
  }, [newestArticle, qc]);
  useEffect(() => {
    if (newestContent === null) return;
    // The doctor's lists (["orgs", id, "deliverables"]) and the admin board (["admin", "pipeline", …]).
    void qc.invalidateQueries({ predicate: (q) => q.queryKey.includes("deliverables") || q.queryKey[1] === "pipeline" });
  }, [newestContent, qc]);
  const read = useMutation({
    mutationFn: async (id: string) => {
      await api.http.POST("/v1/me/notifications/{notification_id}/read", { params: { path: { notification_id: id } } });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: bellKey }),
  });
  const readAll = useMutation({
    mutationFn: async () => {
      await api.http.POST("/v1/me/notifications/read-all");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: bellKey }),
  });

  // Close on Escape or a click outside.
  useEffect(() => {
    if (!open) return;
    const key = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    const click = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", key);
    document.addEventListener("mousedown", click);
    return () => {
      document.removeEventListener("keydown", key);
      document.removeEventListener("mousedown", click);
    };
  }, [open]);

  const unread = inbox.data?.unread ?? 0;
  return (
    <div ref={root} className={cn("relative", className)}>
      <Button
        variant="ghost"
        size="icon"
        aria-label={unread > 0 ? t("notify.bellUnread", { n: unread }) : t("notify.bell")}
        aria-expanded={open}
        aria-controls={panel}
        onClick={() => setOpen((v) => !v)}
      >
        <Bell className="h-5 w-5" aria-hidden />
        {unread > 0 ? (
          <span aria-hidden className="absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-coral-500 px-1 text-[10px] font-bold text-white">
            {unread > 99 ? "99+" : unread}
          </span>
        ) : null}
      </Button>
      {open ? (
        <div id={panel} role="dialog" aria-label={t("notify.title")} className="absolute left-0 top-11 z-50 grid w-80 max-w-[calc(100vw-2rem)] gap-1 rounded-[var(--radius-lg)] border border-border bg-popover p-2 text-foreground shadow-2xl shadow-black/40 ring-1 ring-black/5">
          <div className="flex items-center justify-between px-2 py-1">
            <p className="text-sm font-semibold">{t("notify.title")}</p>
            {unread > 0 ? (
              <Button size="sm" variant="ghost" loading={readAll.isPending} onClick={() => readAll.mutate()}>{t("notify.readAll")}</Button>
            ) : null}
          </div>
          {inbox.isPending ? <Spinner label={t("common.loading")} /> : null}
          {inbox.error ?? read.error ?? readAll.error ? (
            <p role="alert" className="px-2 text-xs text-destructive">{errorMessage(inbox.error ?? read.error ?? readAll.error, t)}</p>
          ) : null}
          {inbox.data?.items.length === 0 ? <p className="px-2 py-6 text-center text-sm text-muted-foreground">{t("notify.empty")}</p> : null}
          <ul className="grid max-h-96 gap-1 overflow-y-auto">
            {inbox.data?.items.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => {
                    if (!n.read) read.mutate(n.id);
                    setOpen(false);
                    nav.navigate(n.link);
                  }}
                  className={cn("grid w-full gap-0.5 rounded-md px-2 py-2 text-left text-sm hover:bg-muted", !n.read && "bg-primary/10")}
                >
                  <span className={cn(!n.read && "font-medium")}>{notificationText(t, locale, n)}</span>
                  <span className="text-xs text-muted-foreground">{formatRelative(locale, n.created_at)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
